import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronDown, MapPin, Calendar, Users, Loader2 } from "lucide-react";
import ShareMenu from "@/components/ShareMenu";
import { EventCTA } from "@/components/EventCTA";
import { setMeta, setCanonical, injectLd, clearLd } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.picklebullpen.com";
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

function DivisionBlock({ d, open, onToggle }) {
  return (
    <div className="border border-zinc-800 mb-2">
      <button onClick={onToggle} data-testid={`roster-division-${slugify(d.label)}`}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-900 transition-colors">
        <span className="font-bold uppercase tracking-wide text-sm text-lime-300">{d.label}</span>
        <span className="flex items-center gap-3 text-xs text-zinc-500">
          {d.entry_count} {d.entry_count === 1 ? "team" : "teams"}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <ol className="px-4 pb-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {d.teams.map((t, i) => (
            <li key={i} className="text-sm text-zinc-200 flex gap-2 border-b border-zinc-900 pb-1.5">
              <span className="text-zinc-600 font-mono text-xs w-6 shrink-0 pt-0.5">{i + 1}.</span>{t}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function EventRoster({ ev, expanded, single }) {
  const [open, setOpen] = useState(expanded);
  const [openDivs, setOpenDivs] = useState(() => (expanded ? new Set(ev.divisions.map((d) => d.label)) : new Set()));
  const toggleDiv = (l) => setOpenDivs((s) => { const n = new Set(s); n.has(l) ? n.delete(l) : n.add(l); return n; });
  return (
    <article className="border border-zinc-800 bg-zinc-950 mb-4" data-testid={`roster-event-${ev.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-4 p-4 sm:p-5 text-left hover:bg-zinc-900/50 transition-colors">
        {ev.logo && <img src={ev.logo} alt={`${ev.name} logo`} className="w-14 h-14 object-contain shrink-0" />}
        <div className="flex-1 min-w-[200px]">
          <h2 className="font-display font-black uppercase text-xl sm:text-2xl leading-none">{ev.name}</h2>
          <div className="flex flex-wrap gap-4 text-xs text-zinc-500 mt-1.5">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{ev.date}{ev.end_date ? ` – ${ev.end_date}` : ""}</span>
            {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{ev.player_count} players · {ev.entry_count} entries</span>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5">
          {ev.divisions.length === 0 ? (
            <p className="text-sm text-zinc-600 py-2">Roster coming soon.</p>
          ) : ev.divisions.map((d) => (
            <DivisionBlock key={d.label} d={d} open={openDivs.has(d.label)} onToggle={() => toggleDiv(d.label)} />
          ))}
          <div className="flex flex-wrap gap-4 mt-4 text-xs">
            <Link to={`/brackets/${ev.id}`} className="text-lime-400 uppercase tracking-widest font-bold hover:underline" data-testid={`roster-brackets-link-${ev.id}`}>View Live Brackets →</Link>
            <Link to={`/court-board/${ev.id}`} className="text-cyan-300 uppercase tracking-widest font-bold hover:underline">Live Court Board →</Link>
            {!single && <Link to={`/rosters/${slugify(`${ev.name}-${ev.location || ""}`)}-${ev.id}`} className="text-zinc-500 uppercase tracking-widest font-bold hover:underline">Roster Page →</Link>}
          </div>
        </div>
      )}
    </article>
  );
}

export default function PublicRoster() {
  const { tid } = useParams();
  const [events, setEvents] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const req = tid
      ? axios.get(`${API}/public/rosters/${tid}`).then(({ data }) => [data])
      : axios.get(`${API}/public/rosters`).then(({ data }) => data);
    req.then(setEvents).catch(() => setMissing(true));
  }, [tid]);

  useEffect(() => {
    if (!events?.length) return;
    const single = tid ? events[0] : null;
    const title = single
      ? `${single.name} — Player Roster & Divisions | Pickle Bullpen`
      : "Pickleball Tournament Rosters — Players & Divisions | Pickle Bullpen";
    document.title = title;
    const desc = single
      ? `Official player roster for ${single.name} in ${single.location || "Florida"} on ${single.date}. ${single.player_count} players across ${single.divisions.length} divisions. Live brackets and scores on Pickle Bullpen.`
      : "Browse official player rosters for Pickle Bullpen pickleball tournaments — every event, division and registered team, with live brackets and court boards.";
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    if (single?.logo) setMeta("property", "og:image", single.logo);
    setCanonical(`${SITE}${single ? `/rosters/${slugify(`${single.name}-${single.location || ""}`)}-${single.id}` : "/rosters"}`);
    injectLd("roster", events.map((ev) => ({
      "@context": "https://schema.org", "@type": "SportsEvent",
      name: ev.name, startDate: ev.date, endDate: ev.end_date || ev.date,
      sport: "Pickleball",
      location: { "@type": "Place", name: ev.location || "Florida" },
      image: ev.logo || ev.image || undefined,
      organizer: { "@type": "Organization", name: ev.director_name || "Pickle Bullpen", logo: ev.logo || undefined },
      url: `${SITE}/rosters/${slugify(`${ev.name}-${ev.location || ""}`)}-${ev.id}`,
    })));
    return () => clearLd("roster");
  }, [events, tid]);

  if (missing) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-3" data-testid="roster-not-found">
      <p className="uppercase tracking-widest text-zinc-500">Roster not published yet</p>
      <Link to="/rosters" className="text-lime-400 text-sm underline">All Tournament Rosters</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-4 sm:px-6 py-8" data-testid="public-roster-page">
      <div className="max-w-5xl mx-auto">
        <nav className="mb-6 text-xs uppercase tracking-widest text-zinc-500 flex flex-wrap items-center justify-between gap-3">
          <span>
            <Link to="/" className="hover:text-lime-400 transition-colors">Pickle Bullpen</Link>
            <span className="mx-2 text-zinc-700">/</span>
            {tid ? <Link to="/rosters" className="hover:text-lime-400 transition-colors inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Rosters</Link> : "Tournament Rosters"}
          </span>
          <div className="border border-zinc-800 px-3 py-2 normal-case tracking-normal">
            <ShareMenu url={window.location.href}
              text={tid && events?.[0] ? `📋 Official player roster — ${events[0].name} on Pickle Bullpen!` : "📋 Pickleball tournament rosters on Pickle Bullpen"}
              testId="roster-share" label="Share roster" />
          </div>
        </nav>
        <h1 className="font-display font-black uppercase leading-tight text-3xl sm:text-4xl mb-1" data-testid="roster-title">
          {tid && events?.[0] ? `${events[0].name} — Official Roster` : "Tournament Rosters"}
        </h1>
        <p className="text-zinc-500 text-xs uppercase tracking-widest mb-8">
          {tid && events?.[0] ? `${events[0].date} · ${events[0].location || ""} · every registered team by division` : "Every launched event — expand to see divisions and registered teams"}
        </p>

        {!events ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading rosters…</div>
        ) : events.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-10 text-center text-zinc-600 text-sm" data-testid="roster-empty">
            No public rosters yet — check back when the next event launches.
          </div>
        ) : (
          events.map((ev, i) => <EventRoster key={ev.id} ev={ev} expanded={!!tid || i === 0} single={!!tid} />)
        )}

        <EventCTA eventName={tid && events?.[0] ? events[0].name : null} />
      </div>
    </div>
  );
}
